<template>
	<div class="xs-12">
		<!-- Headline -->
		<div class="flex xs-12 xs-between">
			<div class="fs5 lh125 fw7 c1 mb2">{{ $t("facts") }}</div>
			<j-button v-if="canEditApprovedClaims(claimStatus)" type="tertiary" @click="$modal.show('add_facts')">
				<crm-icon name="add" color="c2" size="small" />
				{{ $t("add") }}
			</j-button>
		</div>

		<!-- Data -->
		<j-card class="bg5">
			<template v-if="computedFacts.length > 0">
				<j-list :list="computedFacts">
					<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
						<div class="flex wrap xs-8 xs-left xs-middle fw7 lh125">
							<div class="xs-12 c6 lh125">
								{{ item.name ? item.name : item.type }}
							</div>
							<div class="xs-12 lh125 c1 fw4">{{ item.description }}</div>
							<div class="xs-12 lh125 c1 fw4">{{ item.detailedVallue }}</div>
							<div class="fs4 lh125 c1 fw7 mt1">
								{{ item.claimedValue ? item.claimedValue : "---" }}
								{{ item.currencyCode }}
							</div>
						</div>
						<div class="flex xs-4 xs-right xs-middle lh125">
							<!-- TODO: we should be able to edit one fact but for that we need to get schema for fact field so that we know what UI to display -->
							<!-- <crm-icon
								name="edit"
								size="small"
								color="c6"
								class="cu2"
								@click.native="
									factIdToUpdate = item.id
									$modal.show('update_fact')
								"
							/> -->
							<crm-icon
                v-if="canEditApprovedClaims(claimStatus)"
								name="delete"
								size="small"
								color="cError"
								class="cu2"
								@click.native="onRemove(item.id)"
							/>
						</div>
					</div>
				</j-list>
			</template>
			<div class="p2 ta2 c6 fs3" v-else>
				{{ $t("errors.no_data") }}
			</div>
		</j-card>

		<!-- Add modal -->
		<core-modal name="add_facts" :headline="$t('add')" width="800">
			<div class="p4 bg4">
				<add-facts-to-claim @input="factsToAddToClaim = $event" />
				<div class="flex xs-right">
					<j-button @click="onAddFacts" :isLoading="isLoading" class="mt6">
						{{ $t("add_facts") }}
					</j-button>
				</div>
			</div>
		</core-modal>

		<!-- Update modal -->
		<core-modal name="update_fact" :headline="$t('update')">
			<div class="p4 bg4">
				<update-claim-partial
					:hasName="false"
					:hasDescription="false"
					:hasCurrency="false"
					:data="computedFactToUpdate"
					@input="updatedFactPayload = $event"
				/>
				<div class="flex xs-right">
					<j-button
						@click="onFactUpdate"
						:isLoading="isUpdateFactLoading"
						class="mt6"
					>
						{{ $t("update") }}
					</j-button>
				</div>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JList from "@/components/JList"
import JCard from "@/components/JCard"
import CrmIcon from "@/components/CrmIcon"
import JButton from "@/components/JButton"
import CoreModal from "@/modules/core/components/CoreModal"
import AddFactsToClaim from "./AddFactsToClaim"
import UpdateClaimPartial from "./UpdateClaimPartial"

import api from "../api/"
import { unCamelCase } from "@/utils/"
import dayjs from "dayjs"
import { canEditApprovedClaims } from "../utils/"


export default {
	components: {
		JList,
		JCard,
		CrmIcon,
		JButton,
		CoreModal,
		AddFactsToClaim,
		UpdateClaimPartial
	},
	props: {
		data: {
			type: Array || Null
		},
		claimId: {
			type: String
		},
		policyId: {
			type: String
    },
    claimStatus: {
      type: String
    }
	},

	data() {
		return {
			isLoading: false,

			// List of facts to add
			factsToAddToClaim: null,

			// Fact update
			isUpdateFactLoading: false,
			factIdToUpdate: null, // save id when clickin on one of the benefits so that we know which benefit to pass to modal
			updatedFactPayload: null // updated data for the benefit
		}
	},
	computed: {
		computedFacts() {
			return this.data.map(item => {
				/**
				 * It will parse scalarValue to properly display Number, String, Boolean, Date
				 */
				const formatClaimedValue = scalarValue => {
					if (!scalarValue) return null

					// Determine dataType first
					let dataType = null // would become number, string, date, boolean
					Object.keys(scalarValue).find(key => {
						if (scalarValue[key] !== null) dataType = key.replace("Value", "")
					})

					// Format date
					if (dataType === "date")
						return dayjs(scalarValue.dateValue).format(
							this.$store.state.settings.dateFormat
						)

					// Format boolean
					if (dataType === "boolean") {
						if (scalarValue.booleanValue === true) return this.$t("yes")
						if (scalarValue.booleanValue === false) return this.$t("no")
					}

					// Everything else display how it is
					return scalarValue[`${dataType}Value`]
				}

				/**
				 * @description helper fn that will remove all keys of value null from scalarValue object
				 */
				const sanitizeScalarValue = scalarValue => {
					if (!scalarValue) return null
					const output = {}
					Object.keys(scalarValue).filter(key => {
						if (scalarValue[key] !== null) output[key] = scalarValue[key]
					})
					return output
				}

				return {
					id: item.id,
					type: item.type,
					formattedValue: item.formattedValue,
					description: item.benefitDescription || item?.benefit?.description,
					name: unCamelCase(item.type) || item?.benefit?.name, // we are not getting facts.name so we are displaying unCamelCased type
					currencyCode: item.currencyCode,
					claimedValue: formatClaimedValue(item.value), // coomputed value for display
					value: sanitizeScalarValue(item.value) // real value that holds data
				}
			})
		},

		computedFactToUpdate() {
			return this.computedFacts.find(item => item.id === this.factIdToUpdate)
    }
	},
	methods: {
		async onRemove(factId) {
			const res = await api.removeFactFromClaim({
				claimId: this.claimId,
				factId
			})

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.$emit("deleted")
		},

		async onAddFacts() {
			this.isLoading = true
			const addedFacts = await Promise.all(
				this.factsToAddToClaim.map(item =>
					api.addFactToClaim({
						claimId: this.claimId,
						input: {
							type: item.type || null,
							value: { [`${item.dataType.toLowerCase()}Value`]: item.value }
						}
					})
				)
			)

			// Check errors
			if (
				addedFacts.some(
					item => item?.data?.addFactToClaim?.status !== "success"
				)
			) {
				this.$store.dispatch("showError", [this.$t("errors.general")])
				this.isLoading = false
				return
			}

			// Success
			this.isLoading = false
			this.factsToAddToClaim = null
			this.$modal.hide("add_facts")
			this.$emit("added")
		},

		async onFactUpdate() {
			if (!this.updatedFactPayload) return
			const { id, value } = this.updatedFactPayload

			this.isUpdateFactLoading = true
			const res = await api.updateClaimFact({
				claimId: this.claimId,
				factId: id,
				input: {
					value
				}
			})
			this.isUpdateFactLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.$emit("updated")
			this.$modal.hide("update_fact")
			this.updatedFactPayload = null // reset
    },
    
    canEditApprovedClaims: canEditApprovedClaims
	}
}
</script>